import { CSSProperties, useContext, useEffect, useState } from "react";
import YouTubeConnect from "../connect/YouTube";
import VideoSelector from "./VideoSelector";
import dayjs from "dayjs";
import { NavbarContext } from "../../pages/MarketingPage/Data/NavbarProvider";
import { isYouTubeVideoUrl, pullVideoIdFromUrl, queryYouTubeApi } from "helpers/YouTube";
import SmallCard from "./SmallCard";
import { Severity } from "modules/Const";
import useScreen from "Hooks/useScreen";
import ButtonSymphony from "../../elements/ButtonSymphony";
import { connectToBrand_api } from "api/brandApi";
import InputSymphony from "../../elements/InputSymphony";
import CardSymphony from "../../elements/CardSymphony";
import Icon from "elements/Icon";

interface VideoSelectorTabProps {
    videoSelected: any;
    setVideoSelected: any;
    nextTab?: () => void;
    youtubeConnected: any;
    brand: any
    showNextButton?: boolean
}

const THROTTLED_MODE = false;  // You can set this value based on your requirements.

const VideoSelectorTab = ({
    videoSelected,
    setVideoSelected,
    nextTab,
    youtubeConnected,
    brand,
    showNextButton = true
}: VideoSelectorTabProps) => {
    const { mobileView, mStyle } = useScreen();
    const {
        nextButtonHandler,
        setNextButtonHandler,
    } = useContext(NavbarContext);

    const [currentView, setCurrentView] = useState<any>('record-search')

    // youtube video searcher
    const [searchResults, setSearchResult] = useState([]);
    const [searchText, setSearchText] = useState<string>('');
    const [isLoadingSearch, setIsLoadingSearch] = useState<boolean>(false)

    // stores the users recently loaded videos
    const [userDefaultVideos, setUserDefaultVideos] = useState<any>([])

    // youtube searcher
    const [youtubeSearchResults, setYoutubeSearchResult] = useState<any>([]);
    const [youtubeConnectSearchString, setYoutubeConnectSearchString] = useState('');
    const [locallySelectedChannel, setLocallySelectedChannel] = useState<any>(null)
    // releases puller
    const [loadedReleases, setReleases] = useState<any>(false)

    const selectYoutubeArtist = async (item: any) => {
        try {
            setLocallySelectedChannel(item)
            setCurrentView('record-search')
            await connectToBrand_api({
                brandId: brand.id,
                brandSlug: brand.slug,
                service: 'youtube_channel',
                value: item
            })
        } catch (err) {
            console.error('error creating brand', err)
        }
    }

    // pull recent releases if artist has spotify connected
    const loadRecentReleases = async () => {

        var channelId = youtubeConnected ? youtubeConnected.id : locallySelectedChannel ? locallySelectedChannel.id : null

        if (channelId) {
            setIsLoadingSearch(true)

            // Step 1: Get the channel's upload playlist ID
            const channelDetails = await queryYouTubeApi({
                endpoint: '/channels',
                params: {
                    id: channelId,
                    part: 'contentDetails',
                }
            });

            const channelDetailsItems = channelDetails?.items || []

            const uploadPlaylistId = channelDetailsItems[0]?.contentDetails?.relatedPlaylists?.uploads;

            if (uploadPlaylistId) {
                // Step 2: Get the videos from the upload playlist
                const playlistItems = await queryYouTubeApi({
                    endpoint: '/playlistItems',
                    params: {
                        playlistId: uploadPlaylistId,
                        part: 'snippet',
                        maxResults: 30,
                    }
                });

                const items = playlistItems?.items || []

                if (items.length) {
                    const mapped = items.map((o: { snippet: { resourceId: { videoId: any; }; }; }) => ({
                        ...o.snippet,
                        id: o.snippet.resourceId.videoId
                    }));
                    setUserDefaultVideos(mapped)
                    setSearchResult(mapped);
                }
            }

            setIsLoadingSearch(false)
        }
    }

    useEffect(() => {
        // load recent releases
        if (!THROTTLED_MODE && (locallySelectedChannel || youtubeConnected) && (!searchResults || searchResults.length === 0 || !searchText)) {
            loadRecentReleases()
        }
    }, [youtubeConnected, locallySelectedChannel])

    // review selected rrecord
    useEffect(() => {
        if (videoSelected) {
            setNextButtonHandler({
                handler: nextButtonHandler.handler,
                tab: nextButtonHandler.tab,
                show: true,
                text: 'Next',
                disabled: false,
            })
        } else {
            setNextButtonHandler({
                handler: nextButtonHandler.handler,
                tab: nextButtonHandler.tab,
                show: false,
                text: 'Next',
                disabled: false,
            })
        }
    }, [videoSelected])


    useEffect(() => {
        if (!searchText) {
            if (youtubeConnected) {
                setSearchResult(userDefaultVideos)
            } else {
                setSearchResult([])
            }
        }
    }, [searchText])

    // handles youtube search for a video
    const handleYouTubeSearch = async () => {
        const isVideoUrl = isYouTubeVideoUrl(searchText)
        if (!isVideoUrl || searchText.length < 3) {
            return
        }

        setIsLoadingSearch(true)

        // first confirm if its a channel or just a string
        if (isVideoUrl) {
            const videoId = pullVideoIdFromUrl(searchText)

            const results = await queryYouTubeApi({
                endpoint: '/videos',
                params: {
                    id: videoId,
                    part: 'id,snippet',
                    maxResults: 15,
                }
            })

            const items = results?.items || []

            if (items.length) {
                const formattedItems = items.map((o: any) => {
                    return {
                        ...o.snippet,
                        id: o.id
                    }
                })
                setSearchResult(formattedItems)
            }
        } else {
            const results = await queryYouTubeApi({
                endpoint: '/search',
                params: {
                    type: 'video',
                    q: searchText,
                    part: 'id,snippet',
                    maxResults: 15,
                }
            })
            const items = results?.items || []

            if (items.length) {
                const formattedItems = items.map((o: any) => {
                    return {
                        ...o.snippet,
                        id: o.id.videoId
                    }
                })
                setSearchResult(formattedItems)
            }
        }
        setIsLoadingSearch(false)
    }

    let placeholder = `Enter your YouTube video URL ${youtubeConnected ? 'or select a video from your channel.' : '.'}`
    if (THROTTLED_MODE) {
        placeholder = 'Enter your video URL to find your video details.'
    }

    const renderVideoSelectedState = () => {
        return (
            <div className="videoSelectorSelectedState middle-wrapper">
                <div className="flex flex-row items-center py-4 px-6 lg:px-10 border-b">
                    <div className="w-20 flex-shrink-0">
                        <img className="rounded-md" src={videoSelected.primaryThumbnail} alt="YouTube Thumbnail" />
                    </div>
                    <div className="px-3">
                        <h5 className="text-dark font-bold text-lg flex flex-row justify-center items-center">{videoSelected.name}</h5>
                    </div>
                </div>

                <div className="px-6 lg:px-10 py-5 border-b flex flex-row justify-between">
                    <p className="text-lg font-bold">YouTube Channel</p>
                    <p className="text-lg">{videoSelected.channelTitle}</p>
                </div>
                <div className="px-6 lg:px-10 py-5 border-b flex flex-row justify-between">
                    <p className="text-lg font-bold">Release Date</p>
                    <p className="text-lg">{dayjs(videoSelected.publishedAt).format("M/DD/YYYY")}</p>
                </div>
                <div className="px-6 lg:px-10 py-5 border-b flex flex-row justify-between">
                    <p className="text-lg font-bold">Video URL</p>
                    <a target="_blank" className="text-lg underline w-8/12 text-right break-all" href={`https://www.youtube.com/watch?v=${videoSelected.id}`}>
                        {`https://www.youtube.com/watch?v=${videoSelected.id}`}
                    </a>
                </div>
                <div className="px-6 lg:px-10 py-5 border-b flex flex-row justify-between">
                    <SmallCard
                        severity={Severity.WARNING}
                        text="Please ensure your video doesn't have explicit or shocking content - if it does, it may be denied by YouTube's Ads Review. We reserve the right to charge a 5% processing fee for any refunded campaigns."
                    />
                </div>

                {showNextButton && !mobileView &&
                    <div className="text-center px-4 lg:px-10 py-4">
                        <ButtonSymphony
                            className="videoSelectorTabNextButton"
                            onClick={nextTab}
                        >
                            Next
                        </ButtonSymphony>
                    </div>
                }
            </div>
        )
    }

    const renderVideoSearchState = () => {
        if (videoSelected) {
            return null;
        }

        if (currentView !== 'record-search') {
            return (
                <YouTubeConnect
                    artist={brand}
                    closeModal={() => setCurrentView('record-search')}
                    selectYoutubeChannel={selectYoutubeArtist}
                    setYoutubeSearchResult={setYoutubeSearchResult}
                    youtubeSearchResults={youtubeSearchResults}
                    youtubeSearchString={youtubeConnectSearchString}
                    setYoutubeSearchString={setYoutubeConnectSearchString}
                />
            );
        }

        return (
            <div
                className="videoSelectorTabComponent"
                style={mStyle(sty, 'videoSelectorTabComponent')}
            >
                {mobileView &&
                    <Icon className="youtubeIcon" icon="youtube" size={48} />
                }
                <CardSymphony
                    className="videoSelectorTitle"
                    title="Select the video you want to promote."
                    subtitle="Make sure it's a video URL and not a channel URL."
                    isNoPadding
                />
                <div className="youtubeSearchContainer" style={sty.youtubeSearchContainer}>
                    {!mobileView && <Icon className="youtubeIcon" icon="youtube" size={48} />}
                    <InputSymphony
                        className="videoSelectorInput"
                        placeholder={placeholder}
                        value={searchText}
                        onChange={(valueText) => setSearchText(valueText)}
                        label="YouTube Video URL"
                        isSearch
                        onSearch={handleYouTubeSearch}
                        onEnter={handleYouTubeSearch}
                    />
                </div>
                <VideoSelector
                    youtubeConnected={youtubeConnected || locallySelectedChannel}
                    connectYoutubeButton={() => setCurrentView('youtube-connect')}
                    loadingSearch={isLoadingSearch}
                    searchString={searchText}
                    content={searchResults}
                    onVideoSelected={setVideoSelected}
                />
            </div>
        );
    }

    return (
        <>
            {renderVideoSearchState()}
            {videoSelected && renderVideoSelectedState()}
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    videoSelectorTabComponent: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        padding: 24,
        gap: 16
    },
    videoSelectorTabComponent_mobile: {
        padding: 16
    },
    videoSelectorTitle: {
        fontSize: 24,
        fontWeight: 600,
        color: 'black',
    },
    youtubeSearchContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 8,
        width: '100%'
    }
}

export default VideoSelectorTab;